// ============================================
// src/App/assignedSkillsPullMerge.js
// MA-395 — the decision logic for App.jsx's cloud->local merge effect's
// assignedSkillIds field (the same once-per-session `cloudMergedStudentIdsRef`
// effect that levelsPullMerge.js feeds for math/reading levels). Extracted
// to a plain, DI-friendly function (no React, no DOM), mirroring
// levelsPullMerge.js's split.
//
// WHY THIS REPLACES cloudAssignedSkillsMerge.js's mergeCloudAssignedSkillsIntoProgress:
// that function was UNION-ONLY (local ∪ cloud, always) — correct for the
// teacher-assign case (a teacher's addition should always reach the kid),
// but it means a parent's own UN-assign, made on device A, can NEVER
// propagate to device B: device B's local copy still has the id, union
// keeps it forever. This resolver adds the stamp-based LWW half MA-39/
// MA-375 already built for math/reading levels: when the CLOUD side is
// stamped same-or-newer than this device's own last local edit, cloud is
// trusted EXACTLY (not unioned) — that is what lets a removal propagate.
//
// A device that has NEVER stamped a local edit (no assignedSkillIdsUpdatedAt
// at all — every pre-MA-395 record, or a device that has only ever received
// a teacher assignment) has no "local edit" to defend, so it keeps unioning
// (today's behavior, no regression) — see the `!localStamp` branch. That
// means an un-assign made elsewhere takes ONE EXTRA local pull to reach a
// never-stamped device (the pull that adopts the cloud stamp for the first
// time); every stamped device after that resolves in one hop. Documented,
// accepted, not fixed this slice (ledgered as a residual, not a bug).
//
// `changed` mirrors cloudAssignedSkillsMerge.js's own flag: whether the
// resolved queue differs from the device's OWN prior local queue, so
// App.jsx's cache-invalidation gate (useTodaysMission's assign-queue read,
// 1h staleTime) still fires exactly when something the kid will see
// actually moved — never on every ordinary boot.
// ============================================

import { normalizeAssignedQueue, mergeCloudAssignedSkills } from "../lib/engine/assignedSkills";

/**
 * @param {{
 *   localIds: unknown,
 *   localStamp: string | null | undefined,
 *   cloudIds: unknown,
 *   cloudStamp: string | null | undefined,
 * }} args
 * @returns {{
 *   assignedSkillIds: string[],
 *   assignedSkillIdsUpdatedAt: string | null,
 *   assignedSkillsCloudMirror: string[],
 *   shouldPushLocal: boolean,
 *   changed: boolean,
 * }}
 */
export function resolveAssignedSkillsPullMerge({ localIds, localStamp, cloudIds, cloudStamp }) {
  const local = normalizeAssignedQueue(localIds);
  const cloud = normalizeAssignedQueue(cloudIds);
  // The raw cloud array — regardless of which side "wins" below, this is
  // what the NEXT push's teacher-addition diff (updateAssignedSkillIdsSafe)
  // needs as its baseline.
  const assignedSkillsCloudMirror = cloud;

  const localIsNewer = !!localStamp && (!cloudStamp || localStamp > cloudStamp);
  if (localIsNewer) {
    const assignedSkillIds = mergeCloudAssignedSkills(local, cloud);
    return {
      assignedSkillIds,
      assignedSkillIdsUpdatedAt: localStamp,
      assignedSkillsCloudMirror,
      shouldPushLocal: true,
      changed: !sameQueue(assignedSkillIds, local),
    };
  }

  if (!localStamp) {
    // Never-stamped device: no removal signal of its own to trust yet —
    // union, matching pre-MA-395 behavior (no regression for legacy rows).
    const assignedSkillIds = mergeCloudAssignedSkills(cloud, local);
    return {
      assignedSkillIds,
      assignedSkillIdsUpdatedAt: cloudStamp || null,
      assignedSkillsCloudMirror,
      shouldPushLocal: false,
      changed: !sameQueue(assignedSkillIds, local),
    };
  }

  // Cloud is stamped same-or-newer than this device's own last edit: trust
  // it EXACTLY — this is the branch that lets an un-assign propagate.
  return {
    assignedSkillIds: cloud,
    assignedSkillIdsUpdatedAt: cloudStamp,
    assignedSkillsCloudMirror,
    shouldPushLocal: false,
    changed: !sameQueue(cloud, local),
  };
}

function sameQueue(a, b) {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}
